import React from 'react';
import { Modal, Button, Spin, message } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { prescriptionService } from '../services';
import { getApiErrorMessage } from '../utils/error';
import { formatCurrency } from '../utils/format';
import { PrescriptionItem } from '../types';
import './PrescriptionDetailModal.css';

interface PrescriptionDetailModalProps {
  visible: boolean;
  prescriptionId: number | null;
  onClose: () => void;
}

const PrescriptionDetailModal: React.FC<PrescriptionDetailModalProps> = ({
  visible,
  prescriptionId,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const { data: detailResponse, isLoading } = useQuery({
    queryKey: ['prescription-detail', prescriptionId],
    queryFn: () => {
      if (!prescriptionId) throw new Error('No prescriptionId provided');
      return prescriptionService.getDetail(prescriptionId);
    },
    enabled: !!prescriptionId && visible,
  });

  const detail = detailResponse?.data;

  const exportMutation = useMutation({
    mutationFn: (pk: number) => prescriptionService.exportInvoice(pk),
    onSuccess: () => {
      message.success('Xuất hóa đơn thành công!');
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      queryClient.invalidateQueries({ queryKey: ['prescription-detail', prescriptionId] });
    },
    onError: (error: any) => {
      message.error(getApiErrorMessage(error, 'Xuất hóa đơn thất bại!'));
    },
  });

  const formatDosage = (item: PrescriptionItem) => {
    const parts: string[] = [];
    if (item.morning > 0) parts.push(`sáng ${item.morning} ${item.display_unit}`);
    if (item.noon > 0) parts.push(`trưa ${item.noon} ${item.display_unit}`);
    if (item.afternoon > 0) parts.push(`chiều ${item.afternoon} ${item.display_unit}`);
    if (item.evening > 0) parts.push(`tối ${item.evening} ${item.display_unit}`);
    return parts.length ? `Uống: ${parts.join(', ')}` : (item.note || '');
  };

  if (isLoading) {
    return (
      <Modal open={visible} onCancel={onClose} footer={null} width={900} closable>
        <div className="prescription-modal-loading">
          <Spin size="large" />
        </div>
      </Modal>
    );
  }

  if (!detail) return null;

  return (
    <Modal
      title={`Thông tin chi tiết đơn thuốc - ${detail.code}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      className="prescription-detail-modal"
    >
      <div className="prescription-modal-grid">
        <div className="prescription-section customer-section">
          <h3 className="section-title">Thông tin khách hàng</h3>
          <ul className="bullet-list">
            <li>
              <span className="label">Họ tên:</span>{' '}
              <span className="value">{detail.patient_name?.toUpperCase() || '-'}</span>
            </li>
            <li>
              <span className="label">Địa chỉ:</span>{' '}
              <span className="value">{detail.patient_address || '-'}</span>
            </li>
            <li>
              <span className="label">CCCD / MST:</span>{' '}
              <span className="value">{detail.patient_identity || '-'}</span>
            </li>
          </ul>
        </div>

        <div className="prescription-section service-section">
          <h3 className="section-title">Dịch vụ</h3>
          <ul className="bullet-list">
            <li>
              <span className="label">Tiền công khám:</span>{' '}
              <span className="value price">{formatCurrency(parseFloat(detail.consultation_fee))}</span>
            </li>
            <li>
              <span className="label">Tiền thuốc:</span>{' '}
              <span className="value price">{formatCurrency(parseFloat(detail.medicine_fee))}</span>
            </li>
            <li>
              <span className="label">Hình thức thanh toán:</span>{' '}
              <span className="value">Tiền mặt</span>
            </li>
          </ul>
        </div>

        <div className="prescription-section prescription-section-wide">
          <h3 className="section-title">Toa thuốc</h3>
          <ol className="prescription-numbered-list">
            {detail.items?.map((item, index) => (
              <li key={index} className="prescription-item">
                <div className="prescription-item-header">
                  <span className="item-number">{index + 1}.</span>
                  <span className="medicine-name">
                    {item.medicine_name}
                    {item.route ? ` (${item.route})` : ''}
                  </span>
                  <span className="medicine-quantity">
                    {item.quantity} {item.display_unit}
                  </span>
                </div>
                <div className="prescription-item-instructions">
                  {formatDosage(item)}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="prescription-section action-section">
          <div className="action-buttons">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => exportMutation.mutate(detail.id)}
              loading={exportMutation.isPending}
              disabled={detail.invoice_status === 'exported'}
              block
              className="action-btn"
            >
              Hóa đơn khám & Thuốc
            </Button>
            <div className="action-buttons-row">
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => exportMutation.mutate(detail.id)}
                loading={exportMutation.isPending}
                disabled={detail.invoice_status === 'exported'}
                className="action-btn"
              >
                Hóa đơn khám
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => exportMutation.mutate(detail.id)}
                loading={exportMutation.isPending}
                disabled={detail.invoice_status === 'exported'}
                className="action-btn"
              >
                Hóa đơn thuốc
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PrescriptionDetailModal;
